import {
	FirstDays,
	FlowDirection,
	FlowWindow,
	GoalOutcome,
	InteractionDimension,
	InteractionFilterMode,
	JourneyDateRange,
	JourneyFilterState,
	JourneyMeta,
	JourneyScope,
	TransitionFilterMode,
} from '../../../models/journey-explorer/journey-explorer';

export const COLORS = {
	accent: '#0052cd',
	goal: '#1a7f4b',
	goalSoft: '#e3f4ea',
	miss: '#e5484d',
	churn: '#e5484d',
	churnSoft: '#fdecec',
	green: '#1a7f4b',
	text: '#08234c',
	muted: '#4d6389',
	dim: '#8094b5',
	line: '#c8d4ea',
	raised: '#ffffff',
	card: '#f7fafd',
	interaction: '#0060a8',
	transition: '#6b5bd2',
	fallbackType: '#8094b5',
	compareA: '#0052cd',
	compareB: '#c07a2c',
};

const NOT_SET = 'Not set';
export const DEBOUNCE_MS = 300;

export type MapMode = 'customers' | 'time' | 'median';
export type ExplorerView = 'map' | 'flow' | 'funnel' | 'steps';

export const CHURN_INSIGHTS_ENABLED = false;

export interface FlowSettings {
	direction: FlowDirection;
	anchor: string | null;
	end: string | null;
	steps: number;
	within: FlowWindow;
	applyToAll?: boolean;
}

export const flowScope = ({ applyToAll, direction, anchor, end, steps, within }: FlowSettings): JourneyScope | null => {
	if (!applyToAll || !anchor) {
		return null;
	}
	if (direction !== 'between') {
		return { direction, anchor, steps, within };
	}
	return end && end !== anchor ? { direction, anchor, steps, within, end } : null;
};

export const FLOW_STEP_OPTIONS = [2, 3, 4, 5];

export const FLOW_WINDOWS: { key: FlowWindow; label: string; note?: string }[] = [
	{ key: 'any', label: 'any time' },
	{ key: '30m', label: '30 minutes', note: 'same visit' },
	{ key: '1d', label: '1 day' },
	{ key: '3d', label: '3 days' },
	{ key: '1w', label: '1 week', note: 'weekly plans' },
	{ key: '2w', label: '2 weeks', note: 'bi-weekly plans' },
	{ key: '1m', label: '1 month', note: 'monthly plans' },
	{ key: '3m', label: '3 months' },
	{ key: '6m', label: '6 months' },
	{ key: '1y', label: '1 year' },
];

export const FIRST_DAYS_OPTIONS: { key: FirstDays | null; label: string }[] = [
	{ key: 30, label: '30 days' },
	{ key: 60, label: '60 days' },
	{ key: 90, label: '90 days' },
	{ key: null, label: 'Any time' },
];

export const flowWindowLabel = (within: FlowWindow): string =>
	FLOW_WINDOWS.find((option) => option.key === within)?.label ?? within;

export interface GoalInfo {
	name: string;
	label: string;
	rate: number;
	outcome: GoalOutcome | null;
}

export const goalRatio = (pct: number, goal: GoalInfo): number => (goal.rate > 0 ? pct / goal.rate : 0);

export const goalOf = (state: JourneyFilterState | null): string | null => state?.goal?.interaction ?? null;

export const goalOutcomeOf = (state: JourneyFilterState | null): GoalOutcome | null => state?.goal?.outcome ?? null;

export const withGoal = (state: JourneyFilterState, interaction: string | null): JourneyFilterState => ({
	...state,
	goal: interaction ? { interaction, outcome: null } : null,
});

export const withGoalOutcome = (state: JourneyFilterState, outcome: GoalOutcome | null): JourneyFilterState =>
	state.goal ? { ...state, goal: { ...state.goal, outcome } } : state;

export const GOAL_OUTCOME_LABELS: Record<GoalOutcome, string> = {
	reached: 'Reached the goal',
	not_reached: 'Did not reach the goal',
};

export const joinWords = (words: string[]): string =>
	words.length > 1 ? `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}` : words.join('');

export interface FilterPill {
	key: string;
	label: string;
	color: string;
	remove: (state: JourneyFilterState) => JourneyFilterState;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const INTERACTION_MODE_LABELS: Record<InteractionFilterMode, string> = {
	passed_through: 'Passed through',
	did_not_reach: 'Did not reach',
	started_here: 'Started at',
	ended_here: 'Ended at',
};

const TRANSITION_MODE_LABELS: Record<TransitionFilterMode, string> = {
	went_through: 'Went through',
	did_not_go_through: 'Did not go through',
};

export const displayValue = (value: string | null): string => value ?? NOT_SET;

const includesTerm = (text: string, term: string) => text.toLowerCase().includes(term);

export const searchDimensions = <T extends { label: string; values: (string | null)[] }>(
	dimensions: T[],
	query: string,
	groupLabel: string
): T[] => {
	const term = query.trim().toLowerCase();
	const filled = dimensions.filter((dimension) => dimension.values.length > 0);
	if (!term || includesTerm(groupLabel, term)) {
		return filled;
	}
	return filled
		.map((dimension) =>
			includesTerm(dimension.label, term)
				? dimension
				: { ...dimension, values: dimension.values.filter((value) => includesTerm(displayValue(value), term)) }
		)
		.filter((dimension) => dimension.values.length > 0);
};

export const dataLimits = (meta: JourneyMeta): JourneyDateRange => ({
	from: meta.data_range.min,
	to: meta.data_range.max,
});

export const createFilterState = (meta: JourneyMeta): JourneyFilterState => ({
	customer_start: dataLimits(meta),
	interaction_range: dataLimits(meta),
	customer_filters: [],
	dimension_filters: [],
	interaction_filters: [],
	transition_filters: [],
});

export const isSameRange = (a: JourneyDateRange, b: JourneyDateRange): boolean => a.from === b.from && a.to === b.to;

export const setInteractionFilter = (
	state: JourneyFilterState,
	interaction_name: string,
	mode: InteractionFilterMode
): JourneyFilterState => ({
	...state,
	interaction_filters: [
		...state.interaction_filters.filter((filter) => filter.interaction_name !== interaction_name),
		{ interaction_name, mode },
	],
});

export const setTransitionFilter = (
	state: JourneyFilterState,
	from: string,
	to: string,
	mode: TransitionFilterMode
): JourneyFilterState => ({
	...state,
	transition_filters: [
		...state.transition_filters.filter((filter) => filter.from !== from || filter.to !== to),
		{ from, to, mode },
	],
});

const toggleValue = (values: string[], value: string): string[] =>
	values.includes(value) ? values.filter((item) => item !== value) : [...values, value];

const updateValues = <T extends { values: string[] }>(
	filters: T[],
	matches: (filter: T) => boolean,
	create: () => T,
	update: (values: string[]) => string[]
): T[] => {
	const existing = filters.find(matches);
	if (!existing) {
		const created = create();
		const values = update(created.values);
		return values.length ? [...filters, { ...created, values }] : filters;
	}
	return filters
		.map((filter) => (filter === existing ? { ...filter, values: update(filter.values) } : filter))
		.filter((filter) => filter.values.length > 0);
};

export const toggleCustomerValue = (state: JourneyFilterState, column: string, value: string): JourneyFilterState => ({
	...state,
	customer_filters: updateValues(
		state.customer_filters,
		(filter) => filter.column === column,
		() => ({ column, values: [] }),
		(values) => toggleValue(values, value)
	),
});

export const toggleDimensionValue = (
	state: JourneyFilterState,
	table: string,
	column: string,
	value: string
): JourneyFilterState => ({
	...state,
	dimension_filters: updateValues(
		state.dimension_filters,
		(filter) => filter.table === table && filter.column === column,
		() => ({ table, column, values: [] }),
		(values) => toggleValue(values, value)
	),
});

export const withCustomerValue = (state: JourneyFilterState, column: string, value: string): JourneyFilterState => ({
	...state,
	customer_filters: [
		...state.customer_filters.filter((filter) => filter.column !== column),
		{ column, values: [value] },
	],
});

export const selectedCustomerValues = (state: JourneyFilterState, column: string): string[] =>
	state.customer_filters.find((filter) => filter.column === column)?.values ?? [];

export const selectedDimensionValues = (state: JourneyFilterState, table: string, column: string): string[] =>
	state.dimension_filters.find((filter) => filter.table === table && filter.column === column)?.values ?? [];

export const interactionDimensions = (meta: Pick<JourneyMeta, 'interaction_types'>): InteractionDimension[] => {
	const seen = new Set<string>();
	return meta.interaction_types
		.flatMap((type) => type.dimensions)
		.filter((dimension) => {
			const key = `${dimension.table}.${dimension.column}`;
			if (seen.has(key)) return false;
			seen.add(key);
			return true;
		});
};

export const buildPills = (state: JourneyFilterState, meta: JourneyMeta): FilterPill[] => {
	const limits = dataLimits(meta);
	const pills: FilterPill[] = [];

	if (state.goal?.outcome) {
		pills.push({
			key: 'goal_outcome',
			label: `${GOAL_OUTCOME_LABELS[state.goal.outcome]} (${state.goal.interaction})`,
			color: COLORS.goal,
			remove: (current) => withGoalOutcome(current, null),
		});
	}

	if (!isSameRange(state.customer_start, limits)) {
		pills.push({
			key: 'customer_start',
			label: `Customer start ${formatLongDate(state.customer_start.from)} to ${formatLongDate(
				state.customer_start.to
			)}`,
			color: COLORS.accent,
			remove: (current) => ({ ...current, customer_start: limits }),
		});
	}
	if (!isSameRange(state.interaction_range, limits)) {
		pills.push({
			key: 'interaction_range',
			label: `Interactions ${formatLongDate(state.interaction_range.from)} to ${formatLongDate(
				state.interaction_range.to
			)}`,
			color: COLORS.muted,
			remove: (current) => ({ ...current, interaction_range: limits }),
		});
	}

	state.customer_filters.forEach(({ column, values }) => {
		const label = meta.customer_dimensions.find((dimension) => dimension.column === column)?.label ?? column;
		values.forEach((value) =>
			pills.push({
				key: `customer:${column}:${value}`,
				label: `Customer ${label.toLowerCase()}: ${value}`,
				color: COLORS.accent,
				remove: (current) => toggleCustomerValue(current, column, value),
			})
		);
	});

	const details = interactionDimensions(meta);
	state.dimension_filters.forEach(({ table, column, values }) => {
		const label =
			details.find((dimension) => dimension.table === table && dimension.column === column)?.label ?? column;
		values.forEach((value) =>
			pills.push({
				key: `dimension:${table}:${column}:${value}`,
				label: `Interaction ${label.toLowerCase()}: ${value}`,
				color: COLORS.interaction,
				remove: (current) => toggleDimensionValue(current, table, column, value),
			})
		);
	});

	state.interaction_filters.forEach(({ interaction_name, mode }) =>
		pills.push({
			key: `interaction:${interaction_name}`,
			label: `${INTERACTION_MODE_LABELS[mode]} ${interaction_name}`,
			color: COLORS.interaction,
			remove: (current) => ({
				...current,
				interaction_filters: current.interaction_filters.filter(
					(item) => item.interaction_name !== interaction_name
				),
			}),
		})
	);

	state.transition_filters.forEach(({ from, to, mode }) =>
		pills.push({
			key: `transition:${from}:${to}`,
			label: `${TRANSITION_MODE_LABELS[mode]} ${from} to ${to}`,
			color: COLORS.transition,
			remove: (current) => ({
				...current,
				transition_filters: current.transition_filters.filter((item) => item.from !== from || item.to !== to),
			}),
		})
	);

	state.simplify?.hidden.forEach((name) =>
		pills.push({
			key: `hidden:${name}`,
			label: `Hidden ${name}`,
			color: COLORS.dim,
			remove: (current) => ({
				...current,
				simplify: current.simplify && {
					...current.simplify,
					hidden: current.simplify.hidden.filter((item) => item !== name),
				},
			}),
		})
	);

	state.simplify?.groups.forEach(({ label, names }) =>
		pills.push({
			key: `group:${label}`,
			label: `Grouped ${label} (${names.length})`,
			color: COLORS.dim,
			remove: (current) => ({
				...current,
				simplify: current.simplify && {
					...current.simplify,
					groups: current.simplify.groups.filter((group) => group.label !== label),
				},
			}),
		})
	);

	return pills;
};

const formatLongDate = (iso: string): string => {
	const [year, month, day] = iso.split('-').map(Number);
	return `${day} ${MONTHS[month - 1]} ${year}`;
};

export const formatDate = (iso: string): string => formatLongDate(iso.slice(0, 10));

export const formatDateTime = (iso: string): string => {
	const time = new Date(iso);
	if (Number.isNaN(time.getTime())) {
		return iso;
	}
	const pad = (value: number) => String(value).padStart(2, '0');
	return `${time.getUTCDate()} ${MONTHS[time.getUTCMonth()]} ${time.getUTCFullYear()}, ${pad(
		time.getUTCHours()
	)}:${pad(time.getUTCMinutes())}`;
};

export const isoToUkDate = (iso: string): string => {
	const [year, month, day] = iso.split('-');
	return `${day}/${month}/${year}`;
};

export const formatNumber = (value: number): string => value.toLocaleString('en-GB');

export const formatCompact = (value: number): string => {
	if (value >= 1_000_000) {
		return `${(value / 1_000_000).toFixed(1)}M`;
	}
	if (value >= 1_000) {
		return `${(value / 1_000).toFixed(1)}K`;
	}
	return formatNumber(value);
};

export const formatDuration = (seconds: number | null): string => {
	if (seconds === null) {
		return 'n/a';
	}
	if (seconds < 60) {
		return `${Math.round(seconds)}s`;
	}
	if (seconds < 3_600) {
		return `${Math.round(seconds / 60)}m`;
	}
	if (seconds < 172_800) {
		return `${Math.round(seconds / 3_600)}h`;
	}
	return `${(seconds / 86_400).toFixed(1)}d`;
};

export const formatRelative = (iso: string, now: number = Date.now()): string => {
	const minutes = Math.max(0, Math.floor((now - new Date(iso).getTime()) / 60_000));
	if (minutes < 60) {
		return `${minutes}m ago`;
	}
	const hours = Math.floor(minutes / 60);
	if (hours < 24) {
		return `${hours}h ago`;
	}
	const days = Math.floor(hours / 24);
	if (days < 30) {
		return `${days}d ago`;
	}
	return days < 365 ? `${Math.floor(days / 30)}mo ago` : `${Math.floor(days / 365)}y ago`;
};

export const formatSpan = (seconds: number): string => {
	if (seconds < 3_600) {
		const minutes = Math.max(1, Math.round(seconds / 60));
		return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
	}
	if (seconds < 172_800) {
		const hours = Math.round(seconds / 3_600);
		return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
	}
	return `${Math.round(seconds / 86_400)} days`;
};

export const formatCurrency = (value: number, currency: string): string => {
	try {
		return value.toLocaleString('en-GB', { style: 'currency', currency, maximumFractionDigits: 2 });
	} catch {
		return value.toFixed(2);
	}
};

export const formatCompactCurrency = (value: number, currency: string): string => {
	try {
		return value.toLocaleString('en-GB', {
			style: 'currency',
			currency,
			notation: 'compact',
			maximumFractionDigits: value >= 1_000_000 ? 1 : 0,
		});
	} catch {
		return formatCompact(Math.round(value));
	}
};

export const errorMessage = (error: unknown, fallback: string): string =>
	error instanceof Error && error.message ? error.message : fallback;
